'use client';

import { c as _c } from "react/compiler-runtime";
import { AlertTriangle, ExternalLink } from 'lucide-react';
import type { SdkCompatibilityReason } from '@/app/editor/hooks/useInitApp';
import { sdk } from '@/app/sdk';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { SDK_RELEASE_URL } from '@/const';
interface VersionCheckDialogProps {
  isOpen: boolean;
  currentVersion: string;
  requiredVersion: string;
  reason?: SdkCompatibilityReason;
  onRetry?: () => void;
}
const reasonDescriptions: Record<SdkCompatibilityReason, string> = {
  none: 'A manual update is required before continuing.',
  runtimeVersionTooOld: 'Your installed App (SDK) version is below the required runtime version.',
  belowManualFloor: 'This App (SDK) is too old to finish in-app update reliably.',
  updaterUnavailable: 'In-app update is unavailable for this App (SDK) build. Retry Check may still start recovery if your build supports it.',
  updaterFailed: 'In-app update check failed for this App (SDK) build. Use Retry Check to try again.',
  noCompatibleUpdateFound: 'No compatible in-app update was found yet. Retry Check may start an in-app download when one becomes available.',
  updaterReportedError: 'In-app updater reported an error. Use Retry Check to try recovery again, or download manually.',
  unknownVersion: 'App (SDK) version could not be verified reliably.'
};
export const VersionCheckDialog = t0 => {
  const $ = _c(23);
  const {
    isOpen,
    currentVersion,
    requiredVersion,
    reason: t1,
    onRetry
  } = t0;
  const reason = t1 === undefined ? "none" : t1;
  const handleDownload = _temp;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <DialogTitle className="text-primary text-2xl flex items-center gap-2"><AlertTriangle className="h-6 w-6 shrink-0" />Manual Update Required</DialogTitle>;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== currentVersion) {
    t3 = <div>Your Genesys App (SDK) version <strong>{currentVersion}</strong> cannot continue with this editor build.</div>;
    $[1] = currentVersion;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== requiredVersion) {
    t4 = <div>The minimum required App (SDK) version is <strong>{requiredVersion}</strong>.</div>;
    $[3] = requiredVersion;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = reasonDescriptions[reason];
  let t6;
  if ($[5] !== t5) {
    t6 = <div>{t5}</div>;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div>Please download and install the latest version manually to continue using the editor.</div>;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t3 || $[9] !== t4 || $[10] !== t6) {
    t8 = <DialogHeader>{t2}<DialogDescription asChild={true} className="space-y-3 text-base"><div>{t3}{t4}{t6}{t7}</div></DialogDescription></DialogHeader>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t6;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== onRetry) {
    t9 = onRetry && <Button variant="outline" onClick={onRetry}>Retry Check</Button>;
    $[12] = onRetry;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Button onClick={handleDownload} className="gap-2"><ExternalLink className="h-4 w-4" />Download Latest App (SDK)</Button>;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== t9) {
    t11 = <div className="flex justify-center gap-2 pt-4">{t9}{t10}</div>;
    $[15] = t9;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== t11 || $[18] !== t8) {
    t12 = <DialogContent className="sm:max-w-lg gap-4 p-6" showCloseButton={false} onEscapeKeyDown={_temp2} onPointerDownOutside={_temp3} onInteractOutside={_temp4}>{t8}{t11}</DialogContent>;
    $[17] = t11;
    $[18] = t8;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== isOpen || $[21] !== t12) {
    t13 = <Dialog open={isOpen} modal={true}>{t12}</Dialog>;
    $[20] = isOpen;
    $[21] = t12;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  return t13;
};
VersionCheckDialog.displayName = 'VersionCheckDialog';
async function _temp() {
  if (sdk?.os.openUrl) {
    await sdk.os.openUrl(SDK_RELEASE_URL);
    if (sdk?.window.close) {
      sdk.window.close();
    }
  } else {
    window.open(SDK_RELEASE_URL, "_blank");
  }
}
function _temp2(e) {
  return e.preventDefault();
}
function _temp3(e_0) {
  return e_0.preventDefault();
}
function _temp4(e_1) {
  return e_1.preventDefault();
}